import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useComparison } from "@/hooks/use-comparison";
import {
  ShieldCheck,
  Shield,
  ShieldAlert,
  ShieldX,
  CheckCircle2,
  XCircle,
  Euro,
  Users,
  Building2,
  MapPin,
  Accessibility,
  ExternalLink,
  GitCompareArrows,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";

/* ── helpers ── */
function ratingInfo(rating: string | null) {
  if (!rating) return { label: "Not rated", color: "text-muted-foreground", Icon: ShieldX };
  const map: Record<string, { label: string; color: string; Icon: typeof Shield }> = {
    compliant: { label: "Compliant", color: "text-emerald-700", Icon: ShieldCheck },
    substantially_compliant: { label: "Substantially Compliant", color: "text-amber-700", Icon: Shield },
    non_compliant: { label: "Non-Compliant", color: "text-red-700", Icon: ShieldAlert },
  };
  return map[rating] ?? { label: "Not rated", color: "text-muted-foreground", Icon: ShieldX };
}

function Cell({ children, highlight }: { children: React.ReactNode; highlight?: boolean }) {
  return (
    <td
      className={`px-4 py-3 text-sm text-center align-middle border-r last:border-r-0 ${
        highlight ? "bg-grandpal-green/5" : ""
      }`}
    >
      {children}
    </td>
  );
}

function Row({ label, icon: Icon, children }: { label: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <tr className="border-b last:border-b-0 odd:bg-muted/30">
      <td className="px-4 py-3 text-sm font-medium text-muted-foreground whitespace-nowrap">
        <span className="flex items-center gap-2">
          <Icon size={14} />
          {label}
        </span>
      </td>
      {children}
    </tr>
  );
}

export function ComparisonModal() {
  const { isOpen, setIsOpen, comparedHomes, removeFromComparison } = useComparison();

  if (!isOpen) return null;

  const best = {
    hiqa: comparedHomes.reduce<string | null>((best, h) => {
      const order = ["compliant", "substantially_compliant", "non_compliant"];
      if (!h.inspection_rating) return best;
      if (!best) return h.nh_slug;
      const bestHome = comparedHomes.find((x) => x.nh_slug === best);
      const bestRank = order.indexOf(bestHome?.inspection_rating ?? "");
      return order.indexOf(h.inspection_rating) < bestRank ? h.nh_slug : best;
    }, null),
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-5xl w-full max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b sticky top-0 bg-background z-10">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <GitCompareArrows className="text-grandpal-green" size={20} />
            Side-by-Side Comparison
            <Badge variant="outline" className="ml-2 text-xs font-normal">
              {comparedHomes.length} homes
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-x-auto">
          <table className="w-full">
            <colgroup>
              <col className="w-44" />
              {comparedHomes.map((h) => (
                <col key={h.nh_slug} />
              ))}
            </colgroup>

            <thead>
              <tr className="border-b bg-muted/40">
                <th className="px-4 py-4 text-left text-sm font-medium text-muted-foreground">
                  Category
                </th>
                {comparedHomes.map((home) => (
                  <th key={home.nh_slug} className="px-4 py-4 text-center border-r last:border-r-0">
                    <div className="flex flex-col items-center gap-2">
                      <span className="font-semibold text-sm leading-tight">
                        {home.center_name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {home.town ? `${home.town}, ` : ""}{home.county}
                      </span>
                      <div className="flex items-center gap-1">
                        <Link
                          to={`/nursing-homes/${home.county_slug}/${home.nh_slug}`}
                          onClick={() => setIsOpen(false)}
                          className="text-xs text-grandpal-green hover:underline flex items-center gap-0.5"
                        >
                          View profile <ExternalLink size={10} />
                        </Link>
                        <span className="text-muted-foreground">·</span>
                        <button
                          onClick={() => removeFromComparison(home.nh_slug)}
                          className="text-xs text-muted-foreground hover:text-red-500 transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {/* HIQA Rating */}
              <Row label="HIQA Rating" icon={Shield}>
                {comparedHomes.map((home) => {
                  const { label, color, Icon } = ratingInfo(home.inspection_rating);
                  const isHighlight = best.hiqa === home.nh_slug;
                  return (
                    <Cell key={home.nh_slug} highlight={isHighlight}>
                      <span className={`flex items-center justify-center gap-1.5 font-medium ${color}`}>
                        <Icon size={14} />
                        {label}
                      </span>
                      {isHighlight && (
                        <span className="block text-xs text-grandpal-green mt-0.5 font-medium">Best rated</span>
                      )}
                    </Cell>
                  );
                })}
              </Row>

              {/* Fair Deal */}
              <Row label="Fair Deal" icon={CheckCircle2}>
                {comparedHomes.map((home) => (
                  <Cell key={home.nh_slug}>
                    {home.fair_deal_approved === true ? (
                      <span className="flex items-center justify-center gap-1 text-emerald-700 font-medium">
                        <CheckCircle2 size={14} /> Approved
                      </span>
                    ) : home.fair_deal_approved === false ? (
                      <span className="flex items-center justify-center gap-1 text-muted-foreground">
                        <XCircle size={14} /> Not approved
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">Unknown</span>
                    )}
                  </Cell>
                ))}
              </Row>

              {/* Price Range */}
              <Row label="Weekly Price" icon={Euro}>
                {comparedHomes.map((home) => (
                  <Cell key={home.nh_slug}>
                    {home.price_range ? (
                      <span className="font-medium text-foreground">{home.price_range}</span>
                    ) : (
                      <span className="text-muted-foreground text-xs">Not available</span>
                    )}
                  </Cell>
                ))}
              </Row>

              {/* Bed Count */}
              <Row label="Bed Capacity" icon={Users}>
                {comparedHomes.map((home) => {
                  const beds = home.max_residents || home.max_occupancy;
                  return (
                    <Cell key={home.nh_slug}>
                      {beds ? (
                        <span className="font-medium">{beds} beds</span>
                      ) : (
                        <span className="text-muted-foreground text-xs">Not available</span>
                      )}
                    </Cell>
                  );
                })}
              </Row>

              {/* Category */}
              <Row label="Care Type" icon={Building2}>
                {comparedHomes.map((home) => (
                  <Cell key={home.nh_slug}>
                    {home.category ? (
                      <span className="text-xs bg-muted px-2 py-0.5 rounded-full">
                        {home.category.replace(" Nursing Home", "")}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </Cell>
                ))}
              </Row>

              {/* Location */}
              <Row label="Location" icon={MapPin}>
                {comparedHomes.map((home) => (
                  <Cell key={home.nh_slug}>
                    <span className="text-sm">
                      {[home.town, home.county].filter(Boolean).join(", ") || "—"}
                    </span>
                  </Cell>
                ))}
              </Row>

              {/* Wheelchair accessible */}
              <Row label="Wheelchair Access" icon={Accessibility}>
                {comparedHomes.map((home) => (
                  <Cell key={home.nh_slug}>
                    {home.wheelchair_accessible === true ? (
                      <span className="flex items-center justify-center gap-1 text-emerald-700">
                        <CheckCircle2 size={14} /> Yes
                      </span>
                    ) : home.wheelchair_accessible === false ? (
                      <span className="flex items-center justify-center gap-1 text-muted-foreground">
                        <XCircle size={14} /> No
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">Unknown</span>
                    )}
                  </Cell>
                ))}
              </Row>

              {/* Last inspection */}
              <Row label="Last Inspected" icon={ShieldCheck}>
                {comparedHomes.map((home) => (
                  <Cell key={home.nh_slug}>
                    {home.last_inspection_date ? (
                      <span className="text-sm">
                        {new Date(home.last_inspection_date).toLocaleDateString("en-IE", {
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </Cell>
                ))}
              </Row>
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t flex justify-between items-center">
          <p className="text-xs text-muted-foreground">
            Data sourced from HIQA and NTPF public records.
          </p>
          <Button variant="outline" size="sm" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
